import { forwardRef, type ReactNode, useCallback } from "react";
import {
  styled,
  FormControl,
  FormHelperText,
  Input,
  InputLabel,
  type PopoverOrigin,
  Select as MUISelect,
  type SelectProps as MUISelectProps,
  Paper,
} from "~/common/mui";
import { FontSize, getFontStyle, INPUT_FONT } from "../materialInput/Fonts";
import {
  C_ERROR,
  C_INFORMATION,
  C_ON_SURFACE,
  C_ON_SURFACE_VARIANT,
  C_OUTLINE_VARIANT,
  C_PRIMARY,
  C_PRIMARY_HOVER,
  C_SECONDARY_DISABLED,
} from "../icon/Colors";
import { useBooleanState } from "../../hooks/useBooleanState";

export type SelectProps = MUISelectProps & {
  helperLabel?: ReactNode;
  label?: ReactNode;
  disabled?: boolean;
  "data-test-id"?: string;
  error?: boolean;
  fullWidth?: boolean;
  highlight?: boolean;
  shrink?: boolean;
  hideHelper?: boolean;
};

const StyledInput = styled(Input)({
  ...INPUT_FONT,
  color: C_ON_SURFACE,
  padding: "0",
  "&:before": {
    borderBottomWidth: "1px",
    borderBottomColor: C_OUTLINE_VARIANT,
  },
  "&:after": {
    borderBottomWidth: "1px",
    borderBottomColor: C_PRIMARY,
  },
  "&:hover": {
    "&:before": {
      borderBottom: `1px solid ${C_ON_SURFACE} !important`,
    },
  },
  "& .MuiInput-input": {
    minHeight: INPUT_FONT.lineHeight,
    padding: "8px 0",
    paddingRight: "30px",
  },
});

interface StyledInputLabelProps {
  highlight?: boolean;
}

const StyledInputLabel = styled(InputLabel, {
  shouldForwardProp: (prop) => prop !== "highlight",
})<StyledInputLabelProps>(({ highlight }) => ({
  fontWeight: "normal",
  textTransform: "none",
  ...INPUT_FONT,
  color: C_ON_SURFACE,
  "&.Mui-focused": {
    color: C_PRIMARY,
  },
  "&.MuiInputLabel-shrink": {
    ...getFontStyle(FontSize.BodyLarge),
    color: C_ON_SURFACE_VARIANT,
  },
  "&.Mui-error": {
    color: `${C_ERROR} !important`,
  },
  ...(highlight && {
    color: `${C_INFORMATION} !important`,
  }),
}));

interface StyledSelectProps {
  highlight?: boolean;
  hasError?: boolean;
}

const StyledSelect = styled(MUISelect, {
  shouldForwardProp: (prop) => prop !== "highlight" && prop !== "hasError",
})<StyledSelectProps>(({ highlight, hasError }) => ({
  "& .MuiSelect-select": {
    "&:focus": {
      backgroundColor: "transparent",
    },
  },
  "&.Mui-disabled": {
    color: C_SECONDARY_DISABLED,
  },
  "& .MuiSelect-icon": {
    color: C_PRIMARY,
    fontSize: 15,
    top: "calc(50% - 7.5px)",
    "&:hover": {
      color: C_PRIMARY_HOVER,
    },
  },
  ...(highlight && {
    "&:before": {
      borderBottomColor: `${C_INFORMATION} !important`,
    },
  }),
  ...(hasError && {
    "&:before": {
      borderBottomColor: `${C_ERROR} !important`,
    },
  }),
}));

interface StyledFormHelperTextProps {
  highlight?: boolean;
  isOutlined?: boolean;
}

const StyledFormHelperText = styled(FormHelperText, {
  shouldForwardProp: (prop) => prop !== "highlight" && prop !== "isOutlined",
})<StyledFormHelperTextProps>(({ highlight, isOutlined }) => ({
  ...getFontStyle(FontSize.LabelLarge),
  minHeight: getFontStyle(FontSize.LabelLarge).lineHeight,
  marginTop: "4px",
  color: highlight ? `${C_INFORMATION} !important` : `${C_ON_SURFACE_VARIANT} !important`,
  "&.Mui-error": {
    color: `${C_ERROR} !important`,
  },
  ...(isOutlined && {
    fontSize: "12px !important",
  }),
}));

const StyledPaper = styled(Paper)({
  boxShadow: "none",
  border: `1px solid ${C_OUTLINE_VARIANT}`,
  borderRadius: "0px",
});

/**
 * @deprecated - use TextField instead
 */
export const Select = forwardRef((props: SelectProps, ref) => {
  const {
    label,
    variant,
    helperLabel,
    disabled,
    error,
    fullWidth,
    highlight,
    onChange,
    shrink,
    open,
    onOpen,
    onClose,
    hideHelper,
    MenuProps,
    ...rest
  } = props;

  const { val, setTrue, setFalse } = useBooleanState(false);
  const { val: autoOpen, setTrue: setAutoTrue, setFalse: setAutoFalse } = useBooleanState(true);

  const finalOpen = open ?? val;
  const finalOnOpen = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: React.ChangeEvent<any>) => {
      setTrue();
      // @ts-ignore
      onOpen?.(e);
    },
    [onOpen],
  );
  const finalOnClose = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (e: React.ChangeEvent<any>) => {
      setFalse();
      // @ts-ignore
      onClose?.(e);
    },
    [onClose],
  );

  const isOutlined = props.variant === "outlined";
  const borderColor = highlight ? C_INFORMATION : C_ERROR;
  const MuiOutlinedInputBorder = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: borderColor,
    },
  };

  return (
    <FormControl variant={variant} disabled={disabled} error={error} fullWidth={fullWidth}>
      <StyledInputLabel highlight={highlight} shrink={shrink}>
        {label}
      </StyledInputLabel>
      <StyledSelect
        {...rest}
        label={label}
        variant={variant}
        open={finalOpen}
        onClose={(e) => {
          setAutoFalse();
          finalOnClose(e);
          // just delay the autoOpen after the next focus call was triggered
          window.setTimeout(setAutoTrue);
        }}
        onOpen={finalOnOpen}
        onFocus={(e) => !val && autoOpen && finalOnOpen(e)}
        onChange={onChange}
        highlight={highlight}
        hasError={error}
        sx={highlight || error ? MuiOutlinedInputBorder : {}}
        displayEmpty={shrink}
        input={!isOutlined ? <StyledInput /> : undefined}
        MenuProps={{
          ...SELECT_MENU_POSITION_PARAMS,
          MenuListProps: {
            disablePadding: true,
          },
          ...MenuProps,
          slots: {
            paper: StyledPaper,
            ...MenuProps?.slots,
          },
        }}
        ref={ref}
      />
      {!hideHelper && (
        <StyledFormHelperText highlight={highlight} isOutlined={isOutlined}>
          {helperLabel}
        </StyledFormHelperText>
      )}
    </FormControl>
  );
});

export const SELECT_MENU_POSITION_PARAMS: { anchorOrigin: PopoverOrigin; transformOrigin: PopoverOrigin } = {
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "center",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "center",
  },
};
